import React, { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useIsWebView, useToken, useWebViewLoading, useDoctorRouteGuard } from '../../../shared/hooks';
import { DoctorRouteDialog } from '../../../shared/components/DoctorRouteDialog';
import { useCreditCardInput } from '../hooks';

import './CreditCardInput.css';
import './PaymentCommon.css';

const CreditCardInput: React.FC = () => {
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const { id } = useParams<{ id: string }>();
  const token = useToken();

  // データ取得なし（即座に表示可能）→ isLoading=false でhideLoadingを即時発火
  const contentRef = useWebViewLoading(false);

  // ドクタールート監視ガード
  const { shouldBlock, handleConfirm: handleGuardConfirm } = useDoctorRouteGuard(id, token);

  const {
    cardNumber,
    expiryMonth,
    expiryYear,
    securityCode,
    cardName,
    errors,
    setCardNumber,
    setExpiryMonth,
    setExpiryYear,
    setSecurityCode,
    setCardName,
    validate,
    getCardInfo,
    expiryMonths,
    expiryYears,
  } = useCreditCardInput();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const cardInfo = getCardInfo();
      navigate(`/inquiries/${id}/payment-confirm`, {
        state: cardInfo,
      });
    }
  };

  // 支払方法を変更（支払方法選択画面へ遷移）
  const handleChangePaymentMethod = () => {
    if (!id) return;
    navigate(`/inquiries/${id}/payment-method?mode=change`);
  };

  // ドクタールートに切り替わっていた場合は Flutter ダッシュボードへ強制復帰
  if (shouldBlock) {
    return <DoctorRouteDialog onConfirm={handleGuardConfirm} />;
  }

  return (
    <div ref={contentRef} className="payment-container">
      <main className="payment-content">
        <h2 className="content-title">クレジットカード情報</h2>
        <form noValidate>
          <div className="form-group">
            <label htmlFor="cardNumber">カード番号</label>
            <input
              type="text"
              id="cardNumber"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder="0000 0000 0000 0000"
              maxLength={19}
              inputMode="numeric"
              autoComplete="cc-number"
              className={errors.cardNumber ? 'input-error' : ''}
            />
            {errors.cardNumber && <p className="error-message">{errors.cardNumber}</p>}
          </div>

          <div className="form-group">
            <label>有効期限</label>
            <div className="expiry-group">
              <select
                id="expiryMonth"
                value={expiryMonth}
                onChange={(e) => setExpiryMonth(e.target.value)}
                autoComplete="cc-exp-month"
                className={errors.expiryMonth ? 'input-error' : ''}
              >
                <option value="" disabled>月</option>
                {expiryMonths.map(month => <option key={month} value={month}>{month}</option>)}
              </select>
              <span className="expiry-separator">/</span>
              <select
                id="expiryYear"
                value={expiryYear}
                onChange={(e) => setExpiryYear(e.target.value)}
                autoComplete="cc-exp-year"
                className={errors.expiryYear ? 'input-error' : ''}
              >
                <option value="" disabled>年</option>
                {expiryYears.map(year => <option key={year} value={year}>{year.substring(2)}</option>)}
              </select>
            </div>
            {(errors.expiryMonth || errors.expiryYear) && <p className="error-message">{errors.expiryMonth || errors.expiryYear}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="securityCode">セキュリティコード</label>
            <input
              type="text"
              id="securityCode"
              value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value)}
              placeholder="123"
              maxLength={4}
              inputMode="numeric"
              autoComplete="cc-csc"
              className={`security-code-input ${errors.securityCode ? 'input-error' : ''}`}
            />
            {errors.securityCode && <p className="error-message">{errors.securityCode}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="cardName">カード名義</label>
            <p className="input-hint">半角英字で入力してください</p>
            <input
              type="text"
              id="cardName"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              placeholder="TARO YAMADA"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              className={errors.cardName ? 'input-error' : ''}
            />
            {errors.cardName && <p className="error-message">{errors.cardName}</p>}
          </div>
        </form>
      </main>

      <footer className="payment-footer">
        <button type="button" className="button-primary" onClick={handleSubmit}>
          次へ
        </button>
        <button
          type="button"
          className="button-secondary"
          onClick={handleChangePaymentMethod}
        >
          支払方法を変更
        </button>
        {!isWebView && (
          <button className="button-secondary" onClick={() => navigate(-1)}>
            戻る
          </button>
        )}
      </footer>
    </div>
  );
};

export default CreditCardInput;
